// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
// The action types come from the package that DISPATCHES them, not from dash-store's own copies in
// `deskingActionTypes`. This middleware and `paymentSequenceMiddleware` reason about the same responses one
// chain slot apart, and matching them against two independently-maintained sets of string literals meant a
// rename in offer-redux would take the guard with it and silently leave this metric matching nothing.
import { paymentActionTypes } from '@makemydeal/dr-offer-redux';

// interfaces/types
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// utils
import { isMainPaymentTrigger } from '../utils/paymentActionUtils';
import { createStoreScopeState } from '../utils/storeScopeState';
import { buildUsabilityContext } from '../utils/usabilityContext';
import { trackOutOfOrderPaymentResolved } from '../utils/usabilityTelemetry';

/**
 * Diagnostic-only middleware for the out-of-order payment race (Rally US2045935 / F303836).
 *
 * Emits ONLY when a payment actually resolves **out of order** — i.e. an older
 * request's response is applied AFTER a newer request's response already
 * landed. That is the exact moment stale values overwrite the correct ones,
 * which is where the bug is visible. A benign overlap (newest response lands
 * last) produces no event.
 *
 * **Ordering is reused from the existing correlation telemetry.**
 * `correlationMiddleware` (which runs upstream of the payment middlewares in
 * `getInitialMiddleware`) stamps every user action with a monotonic
 * `meta.actionIndex`. Because the payment request AND its response are both
 * built with that same triggering action as `meta.originalAction`, the
 * resolving action exposes the originating edit's index via
 * `action.meta.originalAction.meta.actionIndex`. We compare each resolving main
 * payment's originating index against the highest index resolved so far; a
 * lower index means a stale response is landing last.
 *
 * The emitted event carries `resolvedActionIndex` + `correlationId`, so it joins
 * to `ReduxAction` / `SelectorDelta` / `StateSnapshotDelta` on the same
 * `actionIndex` — letting an analyst pivot straight to the selector/state values
 * that got clobbered.
 *
 * Pure pass-through — never mutates, blocks, or reorders any action. This
 * middleware is measurement only; `paymentSequenceMiddleware` is what actually
 * prevents the race, and this one is registered UPSTREAM of it so the metric
 * keeps counting the responses the guard discards.
 */

// Highest originating actionIndex whose main payment has resolved so far, keyed
// per real store and then per scope id — see `createStoreScopeState` for why both
// keys are load-bearing. The `__scopeId` half matters for correctness here, not
// just tidiness: a scoped store (Alt Deal Structure slideout) shares its
// `__realStore` with the main deal, so keying on the real store alone let slideout
// payments and main-deal payments advance one shared mark and emit out-of-order
// events against each other.
//
// This middleware runs UPSTREAM of paymentSequenceMiddleware so it keeps measuring
// the underlying race even once the guard is discarding the responses; see the
// ordering comment in the dash app's initialMiddleware.ts.
const getHighWaterMark = createStoreScopeState<{ value: number }>(() => ({ value: -1 }));

// Server-controlled kill switch, aligned with the sibling redux-telemetry
// middlewares: default OFF until AppConfig loads the toggle.
const isTelemetryEnabled = (state: StateTree): boolean =>
    (state.featureToggles as Record<string, boolean | undefined>)?.enableReduxTelemetry ?? false;

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    switch (action?.type) {
        case paymentActionTypes.PAYMENT_RECEIVED:
        case paymentActionTypes.PAYMENT_FAILURE: {
            // Grid / program-quote payment calls are intentionally concurrent, so they are excluded — an
            // out-of-order grid resolution is expected, not a bug. Shared with the sequence guard, which
            // has to draw the same line for the same reason.
            if (isMainPaymentTrigger(action.meta?.originalAction)) {
                const originalAction = action.meta?.originalAction;
                const resolvedActionIndex = originalAction?.meta?.actionIndex;
                // Only actions enriched by correlationMiddleware carry an
                // actionIndex; skip anything else (telemetry off, or a
                // payment triggered by a non-enriched action).
                if (typeof resolvedActionIndex === 'number') {
                    const state = store.getState();
                    if (isTelemetryEnabled(state)) {
                        const mark = getHighWaterMark(store);
                        if (resolvedActionIndex < mark.value) {
                            trackOutOfOrderPaymentResolved(
                                {
                                    resolvedActionIndex,
                                    latestActionIndex: mark.value,
                                    actionIndexGap: mark.value - resolvedActionIndex,
                                    resolvedType: action.type,
                                    correlationId: originalAction.meta.correlationId
                                },
                                buildUsabilityContext(state)
                            );
                        } else {
                            mark.value = resolvedActionIndex;
                        }
                    }
                }
            }
            break;
        }
    }

    return next(action);
};
